import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getAllUsers, updateUser } from '@/api/api'
import type { UserListItem } from '@/api/type'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useSubmitLock } from '@/hooks/useSubmitLock'

type GrantAccessDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  mode: 'product' | 'episode'
  /** 付与対象のプロダクトID または エピソードID 群 */
  targetIds: number[]
  /** ダイアログ見出し下に表示するラベル（例: 選択中のタイトル等） */
  targetLabel?: string
}

const formatUserLabel = (user: UserListItem): string => user.custom_name || user.name || user.email || user.id

export const GrantAccessDialog = ({ open, onOpenChange, mode, targetIds, targetLabel }: GrantAccessDialogProps) => {
  const [users, setUsers] = useState<UserListItem[]>([])
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)
  const { isSubmitting: submitting, run } = useSubmitLock()

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setSelectedUserIds(new Set())
    getAllUsers()
      .then((res) => {
        if (cancelled) return
        // has_full_access のユーザーは既に全件アクセス可能なので除外
        setUsers(res.users.filter((u) => !u.has_full_access))
      })
      .catch((err) => {
        console.error('Failed to load users:', err)
        toast.error('ユーザー一覧の取得に失敗しました')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [open])

  const toggleUser = useCallback((userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const targetCountLabel = useMemo(() => {
    const unit = mode === 'product' ? 'プロダクト' : 'エピソード'
    return `${targetIds.length}件の${unit}`
  }, [mode, targetIds.length])

  const handleSubmit = useCallback(async () => {
    if (selectedUserIds.size === 0 || targetIds.length === 0) return
    await run(async () => {
      const targets = users.filter((u) => selectedUserIds.has(u.id))
      const results = await Promise.allSettled(
        targets.map((user) => {
          if (mode === 'product') {
            const merged = Array.from(new Set([...user.product_ids, ...targetIds]))
            return updateUser(user.id, { product_ids: merged })
          }
          const merged = Array.from(new Set([...user.episode_ids, ...targetIds]))
          return updateUser(user.id, { episode_ids: merged })
        }),
      )
      const failed = results.filter((r) => r.status === 'rejected')
      const succeeded = results.length - failed.length
      if (failed.length > 0) {
        console.error(
          'Failed to grant access for some users:',
          failed.map((r) => (r as PromiseRejectedResult).reason),
        )
        if (succeeded > 0) {
          toast.warning(`${succeeded}人に付与しました（${failed.length}人で失敗）`)
        } else {
          toast.error('アクセス権の付与に失敗しました')
        }
      } else {
        toast.success(`${succeeded}人のユーザーに付与しました`)
      }
      if (succeeded > 0) {
        onOpenChange(false)
      }
    })
  }, [mode, onOpenChange, selectedUserIds, targetIds, users, run])

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting && !next) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>アクセス権限を付与</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {targetCountLabel}
            {targetLabel ? `: ${targetLabel}` : ''}
          </p>
        </DialogHeader>

        <ScrollArea className="max-h-[50vh] -mx-2 px-2">
          {loading ? (
            <p className="py-4 text-center text-sm text-muted-foreground">読み込み中...</p>
          ) : users.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">付与可能なユーザーがいません</p>
          ) : (
            <ul className="space-y-1">
              {users.map((user) => {
                const inputId = `grant-user-${user.id}`
                return (
                  <li key={user.id}>
                    <label
                      htmlFor={inputId}
                      className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-muted/50 cursor-pointer"
                    >
                      <Checkbox
                        id={inputId}
                        checked={selectedUserIds.has(user.id)}
                        onCheckedChange={() => toggleUser(user.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{formatUserLabel(user)}</div>
                        {user.email && <div className="text-xs text-muted-foreground truncate">{user.email}</div>}
                      </div>
                    </label>
                  </li>
                )
              })}
            </ul>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            キャンセル
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || selectedUserIds.size === 0 || targetIds.length === 0}>
            {submitting ? '付与中...' : `付与 (${selectedUserIds.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
